import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import dataServices from "../../apiServices/data.services";
import { useAlert } from "react-alert";
import Loader from "../../components/Loader/Loader";
import { Can } from "../../context/AuthProvider";
import { handleValidationError } from "../../components/CommonFunctions";

const EditPayment = () => {
  const { id } = useParams();
  const [payment, setPayment] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([dataServices.getPaymentDetails(id), dataServices.getSubscriptions()])
      .then(([paymentRes, subscriptionsRes]) => {
        if (paymentRes.status === 200) {
          setPayment(paymentRes.data);
        }
        if (subscriptionsRes.status === 200) {
          setSubscriptions(subscriptionsRes.data);
        }
      })
      .catch((err) => {
        alert.error(handleValidationError(err));
      })
      .finally(() => setLoading(false));
  }, [id, alert]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subscription_id: payment?.subscription_id || payment?.subscription?.id || "",
      amount: payment?.amount || "",
      payment_date: payment?.payment_date || "",
      status: payment?.status || "completed",
      transaction_id: payment?.transaction_id || "",
      description: payment?.description || "",
      discount_amount: payment?.discount_amount || "",
    },
    validationSchema: Yup.object({
      subscription_id: Yup.string().required("Subscription is required"),
      amount: Yup.number().typeError("Amount must be numeric").required("Amount is required"),
      payment_date: Yup.string().required("Payment date is required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      dataServices
        .editPayment(id, values)
        .then((res) => {
          if (res.status === 200) {
            alert.success("Payment updated successfully");
            navigate("/payments");
          }
        })
        .catch((err) => {
          alert.error(handleValidationError(err));
        })
        .finally(() => setLoading(false));
    },
  });

  if (!payment) {
    return <Loader />;
  }

  return (
    <div className="content-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-2 pb-1 fw-bold text-black">Edit Payment</h6>
      </div>
      <Can I="edit" this="Payment">
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="subscription_id">Subscription <span className="text-danger">*</span></Form.Label>
                <Form.Control as="select" id="subscription_id" name="subscription_id" value={formik.values.subscription_id} onChange={formik.handleChange}>
                  <option value="">Select subscription</option>
                  {subscriptions.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      #{sub.id} - {sub.plan?.name || "Plan"}
                    </option>
                  ))}
                </Form.Control>
                {formik.touched.subscription_id && formik.errors.subscription_id ? <div className="invalid-feedback d-block">{formik.errors.subscription_id}</div> : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="amount">Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  id="amount"
                  name="amount"
                  type="text"
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  className={formik.touched.amount && formik.errors.amount ? "form-control is-invalid" : "form-control"}
                />
                {formik.touched.amount && formik.errors.amount ? <div className="invalid-feedback">{formik.errors.amount}</div> : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="payment_date">Payment Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  id="payment_date"
                  name="payment_date"
                  type="date"
                  value={formik.values.payment_date}
                  onChange={formik.handleChange}
                  className={formik.touched.payment_date && formik.errors.payment_date ? "form-control is-invalid" : "form-control"}
                />
                {formik.touched.payment_date && formik.errors.payment_date ? <div className="invalid-feedback">{formik.errors.payment_date}</div> : null}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="status">Status</Form.Label>
                <Form.Control as="select" id="status" name="status" value={formik.values.status} onChange={formik.handleChange}>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="transaction_id">Transaction ID</Form.Label>
                <Form.Control id="transaction_id" name="transaction_id" type="text" value={formik.values.transaction_id} onChange={formik.handleChange} />
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="discount_amount">Discount Amount</Form.Label>
                <Form.Control id="discount_amount" name="discount_amount" type="text" value={formik.values.discount_amount} onChange={formik.handleChange} />
              </Form.Group>
            </Col>
            <Col sm={12}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="description">Description</Form.Label>
                <Form.Control as="textarea" id="description" name="description" rows={3} value={formik.values.description} onChange={formik.handleChange} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button type="button" className="btn btn-secondary w-100" onClick={() => navigate("/payments")}>Cancel</Button>
            </Col>
            <Col className="footer mt-auto">
              <Button type="submit" disabled={loading} className="btn btn-primary w-100">Save Changes</Button>
            </Col>
          </Row>
        </Form>
      </Can>
    </div>
  );
};

export default EditPayment;
